interface AbortableRequest {
  abort: () => void;
  response: Promise<any>;
}
interface ResolvedRequest {
  key: string;
  response: any;
}

export function create(url: string, cb: Function): AbortableRequest {
  const x = new XMLHttpRequest();
  x.open('GET', url, true);
  x.setRequestHeader('Accept', 'application/json');
  let resolved = false;

  const response = new Promise((resolve, reject) => {
    x.onload = e => {
      if (x.readyState === 4) {
        resolved = true;
        if (x.status === 200) {
          const json = JSON.parse(x.response);
          if (cb) resolve(cb(json));
          else resolve(json);
        } else {
          reject(x.statusText);
        }
      }
    };

    x.onerror = () => {
      resolved = true;
      reject(x.statusText);
    };
    x.onabort = () => {
      resolved = true;
      reject(new Error('aborted'));
    };
  });

  const abort = () => {
    if (resolved) {
      return;
    }
    x.abort();
  };
  x.send();

  return {
    abort,
    response,
  };
}

export function createRequestCache() {
  const cachedRequests: ResolvedRequest[] = [];
  const abortable: Record<string, { abort(): void }> = {};

  return {
    find(key: string) {
      return cachedRequests.find(r => r.key === key);
    },

    push(key: string, request: AbortableRequest): Promise<ResolvedRequest> {
      abortable[key] = request;
      return Promise.resolve(request.response).then(response => {
        delete abortable[key];
        const cached = { key, response };
        cachedRequests.push(cached);
        return cached;
      });
    },

    add(key: string, requestFn: () => AbortableRequest) {
      const cached = this.find(key);
      if (cached) {
        return Promise.resolve(cached.response);
      }
      return this.push(key, requestFn()).then(req => req.response);
    },

    abort() {
      Object.keys(abortable).forEach(key => abortable[key].abort());
    },
  };
}
